import { i18n } from "i18next";

import { getSupportedCardTypesListAsGoogleStrings } from "checkout-experience/utils/payment-helpers";
import { OrderSummary } from "checkout-query-client/hooks/use-checkout-session";
import {
  getAllowedPaymentMethods as getAllowedPaymentMethodsQuery,
  getIsReadyToPayRequest as getIsReadyToPayRequestQuery,
  getMerchantInfo,
  getTransactionInfo as getTransactionInfoQuery,
} from "checkout-query-client/utils/google-pay/google-pay-payload";

import {
  CardNetwork,
  GetMerchantSettingsResponse_MerchantSettings,
  GooglePay,
  PlainMessage,
} from "frontend-client";

export type GooglePaymentData = google.payments.api.PaymentData;
export type GoogleAddress = google.payments.api.Address;
export type GoogleEnvironment = google.payments.api.Environment;

type GetTransactionInfoProps = {
  merchantSettings?: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  orderSummary: OrderSummary;
};

export function getTransactionInfo({
  merchantSettings,
  orderSummary,
}: GetTransactionInfoProps): google.payments.api.TransactionInfo {
  return getTransactionInfoQuery({
    country: merchantSettings?.country ?? "US",
    totalAmount: orderSummary.totalAmount,
  });
}

type GetIsReadyToPayRequestProps = {
  googlePaySettings: PlainMessage<GooglePay>;
  merchantSettings: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  googlePayEnv?: string;
};

export function getIsReadyToPayRequest({
  googlePaySettings,
  merchantSettings,
}: GetIsReadyToPayRequestProps): google.payments.api.IsReadyToPayRequest {
  return getIsReadyToPayRequestQuery({
    allowedPaymentMethods: getAllowedPaymentMethods(
      googlePaySettings,
      merchantSettings
    ),
  });
}

export function getAllowedPaymentMethods(
  googlePaySettings: PlainMessage<GooglePay>,
  merchantSettings: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>
):
  | google.payments.api.IsReadyToPayPaymentMethodSpecification[]
  | google.payments.api.PaymentMethodSpecification[] {
  const acceptedCards = (
    merchantSettings?.paymentMethods?.cardPaymentMethod?.supportedCards ?? []
  ).map((m: { network: CardNetwork }) => m.network);
  const allowedCardNetworks =
    getSupportedCardTypesListAsGoogleStrings(acceptedCards);

  return getAllowedPaymentMethodsQuery(googlePaySettings, allowedCardNetworks);
}

type GetPaymentDataRequestProps = {
  merchantSettings: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  googlePaySettings: PlainMessage<GooglePay>;
  orderSummary: OrderSummary;
};

export function getPaymentDataRequest({
  merchantSettings,
  googlePaySettings,
  orderSummary,
}: GetPaymentDataRequestProps): google.payments.api.PaymentDataRequest {
  return {
    apiVersion: 2,
    apiVersionMinor: 0,
    transactionInfo: getTransactionInfo({ merchantSettings, orderSummary }),
    merchantInfo: getMerchantInfo({ googlePaySettings }),
    allowedPaymentMethods: getAllowedPaymentMethods(
      googlePaySettings,
      merchantSettings
    ) as google.payments.api.PaymentMethodSpecification[],
  };
}

type GetGooglePayButtonOptions = {
  googlePaySettings: PlainMessage<GooglePay>;
  i18n: i18n;
  merchantSettings: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  googlePayEnv?: string;
};

export function getGooglePayButtonOptions({
  googlePaySettings,
  i18n,
  merchantSettings,
}: GetGooglePayButtonOptions): Partial<google.payments.api.ButtonOptions> {
  const shortcodeLanguage = i18n.language.split("-")[0];
  const allowedPaymentMethods = getAllowedPaymentMethods(
    googlePaySettings,
    merchantSettings
  );
  return {
    allowedPaymentMethods: allowedPaymentMethods,
    buttonLocale: shortcodeLanguage,
    buttonSizeMode: "fill",
    buttonColor: googlePaySettings.googlePayButton
      ?.buttonColor as google.payments.api.ButtonColor,
    buttonType: googlePaySettings.googlePayButton
      ?.buttonType as google.payments.api.ButtonType,
  };
}
